import { isSameDay, isThisYear } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSearch } from 'react-icons/bi';
import { useSelector } from 'react-redux';

import { isEmpty } from 'lodash-es';
import useMapView from '../../../hooks/useMapView';
import useSearchModal from '../../../hooks/useSearchModal';
import useTimezone from '../../../hooks/useTimezone';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';

const Search = () => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const searchModal = useSearchModal();
  const mapView = useMapView();
  const searchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );

  const selectedPlace = useMemo(() => {
    if (!searchSelection || !searchSelection?.placeLocation) {
      return t('static.header.anywhere_label');
    }

    if (searchSelection && mapView.isOpen) {
      return t('static.header.selected_area_label');
    }

    let result = null;

    if (!isEmpty(searchSelection.placeLocation?.city?.label)) {
      result = result
        ? `${result}, ${searchSelection.placeLocation?.city?.label}`
        : searchSelection.placeLocation?.city?.label;
    }

    if (!isEmpty(searchSelection.placeLocation?.state?.label)) {
      result = result
        ? `${result}, ${searchSelection.placeLocation?.state?.label}`
        : searchSelection.placeLocation?.state?.label;
    }

    if (!isEmpty(searchSelection.placeLocation?.country?.label)) {
      result = result
        ? `${result}, ${searchSelection.placeLocation?.country?.label}`
        : searchSelection.placeLocation?.country?.label;
    }

    if (!result) {
      return t('static.header.anywhere_label');
    }

    return result;
  }, [searchSelection]);

  const selectedPeriod = useMemo(() => {
    if (!searchSelection || !searchSelection.time?.startTime) {
      return t('static.header.anyday_label');
    }

    const targetDate = new Date(searchSelection.time.startTime);

    let formatOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: currentTimezone,
    };
    if (!isThisYear(targetDate)) {
      formatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: currentTimezone,
      };
    }

    let result = null;
    if (
      searchSelection.time?.endTime &&
      !isSameDay(searchSelection.time?.startTime, searchSelection.time?.endTime)
    ) {
      result = `${Intl.DateTimeFormat(i18n.language, formatOptions).format(
        targetDate,
      )} - ${Intl.DateTimeFormat(i18n.language, formatOptions).format(
        new Date(searchSelection.time.endTime),
      )}`;
    } else {
      result = Intl.DateTimeFormat(i18n.language, formatOptions).format(
        targetDate,
      );
    }

    if (!result) {
      return t('static.header.anyday_label');
    }

    return result;
  }, [searchSelection]);

  return (
    <button
      id="header-search-bar"
      key="header-search-bar"
      aria-label={`${selectedPlace} | ${selectedPeriod}`}
      data-test-id="header-search-bar"
      data-umami-event="header-search-bar"
      className="group min-w-full cursor-pointer rounded-full border py-2 shadow-sm transition hover:shadow-md md:w-auto"
      onClick={() => searchModal.onOpen()}
      type="button"
    >
      <div className="flex flex-row items-center justify-between gap-1">
        <div className="grid grow grid-cols-1 text-sm font-semibold capitalize sm:grid-cols-11">
          <div className="line-clamp-1 size-full min-w-full overflow-hidden text-ellipsis break-all px-4 text-center group-hover:font-bold sm:col-span-5">
            {selectedPlace}
          </div>
          <div className="hidden flex-col items-center justify-center px-1 sm:flex">
            <div className="border-l">&nbsp;</div>
          </div>
          <div className="line-clamp-1 size-full min-w-full items-center justify-center overflow-hidden text-ellipsis break-all px-4 text-center group-hover:font-bold sm:col-span-5">
            {selectedPeriod}
          </div>
        </div>
        <div className="hidden flex-row items-center gap-3 pr-2 text-sm font-semibold sm:flex">
          <div className="rounded-full bg-sky-600 p-2 text-white">
            <BiSearch size={18} />
          </div>
        </div>
      </div>
    </button>
  );
};

export default Search;
